/**
 * 通知相关 API
 */
import apiClient from './config';
import type {
  GetNotificationsParams,
  GetNotificationsResult,
  UnreadCountResult,
} from '@/types/notification';

const UNREAD_COUNT_CACHE_TTL_MS = 15 * 1000;
let unreadCountCache: { value: number; expiresAt: number } | null = null;
let unreadCountInflight: Promise<number> | null = null;

/**
 * 获取通知列表
 */
export async function getNotifications(
  params: GetNotificationsParams = {}
): Promise<GetNotificationsResult> {
  try {
    const response = (await apiClient.get('/notifications', {
      params,
    })) as unknown as GetNotificationsResult;
    return response;
  } catch (error) {
    console.error('获取通知列表失败:', error);
    return {
      data: [],
      total: 0,
      page: params.page || 1,
      pageSize: params.pageSize || 20,
    };
  }
}

/**
 * 标记通知已读
 */
export async function markNotificationAsRead(id: string): Promise<boolean> {
  try {
    const response = (await apiClient.put(`/notifications/${id}/read`)) as unknown as {
      success: boolean;
      error?: string;
    };
    if (response.success) {
      unreadCountCache = null;
    }
    return response.success;
  } catch (error) {
    console.error('标记通知已读失败:', error);
    return false;
  }
}

/**
 * 获取未读通知数量
 */
export async function getUnreadNotificationCount(): Promise<number> {
  const now = Date.now();
  if (unreadCountCache && unreadCountCache.expiresAt > now) {
    return unreadCountCache.value;
  }

  if (unreadCountInflight) {
    return unreadCountInflight;
  }

  unreadCountInflight = (async () => {
    try {
      const response = (await apiClient.get('/notifications/unread-count')) as unknown as UnreadCountResult;
      const count = response.count || 0;
      unreadCountCache = {
        value: count,
        expiresAt: Date.now() + UNREAD_COUNT_CACHE_TTL_MS,
      };
      return count;
    } catch (error) {
      console.error('获取未读通知数量失败:', error);
      return 0;
    } finally {
      unreadCountInflight = null;
    }
  })();

  try {
    return await unreadCountInflight;
  } catch (error) {
    console.error('获取未读通知数量失败:', error);
    return 0;
  }
}
